import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { getMyReceipt } from "@/lib/receipts.functions";
import { breakdownFromPayment, formatInr } from "@/lib/gst";

export const Route = createFileRoute("/receipt/$id")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Tax Receipt | WeLivdIt" },
      { name: "description", content: "View and print your WeLivdIt tax invoice with base fee and 18% GST breakdown." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Tax Receipt | WeLivdIt" },
      { property: "og:description", content: "Your WeLivdIt payment receipt with GST breakdown." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ReceiptPage,
  errorComponent: () => <Centered>We couldn't load this receipt. Please sign in and try again.</Centered>,
  notFoundComponent: () => <Centered>Receipt not found.</Centered>,
});

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto max-w-xl px-5 py-20 text-center text-sm text-muted-foreground">
      {children}
      <div className="mt-6">
        <Link to="/student-dashboard" className="text-primary underline underline-offset-4">
          Back to dashboard
        </Link>
      </div>
    </main>
  );
}

type Payment = {
  plan_name: string;
  amount_paise: number;
  base_amount_paise?: number | null;
  gst_amount_paise?: number | null;

  status: string;
  created_at: string;
  buyer_name: string | null;
  buyer_email: string | null;
  buyer_phone: string | null;
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
};

function ReceiptPage() {
  const { id } = Route.useParams();
  const fetchReceipt = useServerFn(getMyReceipt);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");

  useEffect(() => {
    let alive = true;
    fetchReceipt({ data: { id } })
      .then((res) => {
        if (!alive) return;
        if (res.found) {
          setPayment(res.payment as Payment);
          setState("ready");
        } else {
          setState("missing");
        }
      })
      .catch(() => alive && setState("error"));
    return () => {
      alive = false;
    };
  }, [fetchReceipt, id]);

  if (state === "loading") return <Centered>Loading your receipt…</Centered>;
  if (state === "missing") return <Centered>We couldn't find a receipt with that reference on your account.</Centered>;
  if (state === "error" || !payment)
    return <Centered>Please sign in with the email you paid with to view this receipt.</Centered>;

  const { base, gst, total } = breakdownFromPayment(payment);

  return (
    <main className="mx-auto max-w-2xl px-5 py-12 print:py-0">
      <h1 className="font-display text-3xl font-semibold">Tax Invoice</h1>
      <p className="mt-1 text-sm text-muted-foreground">WeLivdIt · US Visa Interview Coaching</p>

      <section className="mt-8 rounded-2xl border border-border bg-card p-6">
        <dl className="space-y-2 text-sm">
          <Row label="Billed to" value={payment.buyer_name || "—"} />
          <Row label="Email" value={payment.buyer_email || "—"} />
          <Row label="Phone" value={payment.buyer_phone || "—"} />
          <Row label="Plan" value={payment.plan_name} />
          <Row label="Date" value={format(new Date(payment.created_at), "dd MMM yyyy, p")} />
          <Row label="Payment reference" value={payment.razorpay_payment_id || payment.razorpay_order_id || "—"} />
          <Row label="Status" value={payment.status} />
        </dl>

        <dl className="mt-6 space-y-2 border-t border-border pt-4 text-sm">
          <Row label="Base fee" value={formatInr(base)} />
          <Row label="GST (18%)" value={formatInr(gst)} />
          <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
            <dt>Total paid</dt>
            <dd>{formatInr(total)}</dd>
          </div>
        </dl>
        <p className="mt-2 text-xs text-muted-foreground">All amounts in INR. 18% GST charged on top of the base fee.</p>
      </section>

      <div className="mt-6 flex gap-3 print:hidden">
        <Button onClick={() => window.print()}>Print / Save as PDF</Button>
        <Button variant="outline" asChild>
          <Link to="/student-dashboard">Back to dashboard</Link>
        </Button>
      </div>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}
